import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import { messages } from "../../../../locales/messages";
import { AnalyticsDashboardResults } from "../../../../utils/analytics/types";
import {
  formatAnalyticsNumber,
  formatAnalyticsPercent,
  parseBoldNumber,
  parsePercent,
} from "../../../../utils/analytics/parsers";
import { analyticsTheme, KPI_ACCENTS } from "../theme";
import { AnalyticsKpiCard } from "./AnalyticsKpiCard";
import { DepartmentEngagementChart } from "./DepartmentEngagementChart";
import { AnalyticsInsightTable } from "./AnalyticsInsightTable";

interface AnalyticsDashboardGridProps {
  data?: AnalyticsDashboardResults;
  isLoading: boolean;
}

const SectionLabel = ({ children }: { children: string }) => (
  <Typography
    sx={{
      fontSize: 12.5,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: analyticsTheme.text.muted,
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

export const AnalyticsDashboardGrid = ({
  data,
  isLoading,
}: AnalyticsDashboardGridProps) => {
  const { t } = useTranslation();
  const emptyLabel = t(messages.Analytics_NoData());

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
      <Box>
        <SectionLabel>
          {t(messages.Analytics_PerformanceOverview())}
        </SectionLabel>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2.5,
          }}
        >
          <AnalyticsKpiCard
            label={t(messages.Analytics_TotalViews())}
            value={formatAnalyticsNumber(parseBoldNumber(data?.totalViews))}
            icon={<VisibilityOutlinedIcon />}
            accent={KPI_ACCENTS[0]}
            isLoading={isLoading}
            testId="analytics-kpi-total-views"
          />
          <AnalyticsKpiCard
            label={t(messages.Analytics_TotalClicks())}
            value={formatAnalyticsNumber(parseBoldNumber(data?.totalClicks))}
            icon={<AdsClickOutlinedIcon />}
            accent={KPI_ACCENTS[1]}
            isLoading={isLoading}
            testId="analytics-kpi-total-clicks"
          />
          <AnalyticsKpiCard
            label={t(messages.Analytics_TotalSaves())}
            value={formatAnalyticsNumber(parseBoldNumber(data?.totalSaves))}
            icon={<BookmarkBorderOutlinedIcon />}
            accent={KPI_ACCENTS[2]}
            isLoading={isLoading}
            testId="analytics-kpi-total-saves"
          />
          <AnalyticsKpiCard
            label={t(messages.Analytics_AvgCTR())}
            value={formatAnalyticsPercent(parsePercent(data?.avgCTR))}
            icon={<PercentOutlinedIcon />}
            accent={KPI_ACCENTS[3]}
            isLoading={isLoading}
            testId="analytics-kpi-avg-ctr"
          />
        </Box>
      </Box>

      <Box>
        <SectionLabel>{t(messages.Analytics_Insights())}</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DepartmentEngagementChart
            title={t(messages.Analytics_DepartmentEngagement())}
            response={data?.departmentEngagement}
            isLoading={isLoading}
            emptyLabel={emptyLabel}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
              gap: 2.5,
            }}
          >
            <AnalyticsInsightTable
              title={t(messages.Analytics_HotDeals())}
              icon={<LocalFireDepartmentOutlinedIcon />}
              response={data?.hotDeals}
              isLoading={isLoading}
              emptyLabel={emptyLabel}
              testId="analytics-table-hot-deals"
            />
            <AnalyticsInsightTable
              title={t(messages.Analytics_TopPerformingDeals())}
              icon={<EmojiEventsOutlinedIcon />}
              response={data?.topPerformingDeals}
              isLoading={isLoading}
              emptyLabel={emptyLabel}
              testId="analytics-table-top-performing-deals"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
